import { useState } from 'react';
import { Button, Input, Label } from '@artstorefronts/ui';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { ASK_TYPES, askPath, imageCount, type AskType } from '@arthelper/shared/community-ask';

import AdminLayout from '../../../components/AdminLayout';
import { ASK_LABELS, LINK_GROUPS, type LinkHost, type OneClickLink } from './catalog';

/** Production hosts, for reference next to every link; the links themselves use this environment's. */
const HOST_LABEL: Record<LinkHost, string> = {
  community: 'Community · arthelper.com',
  app: 'App · app.arthelper.ai',
};

function originFor(host: LinkHost): string {
  if (host === 'community') {
    return (__TOWN_SQUARE_URL__ || 'https://www.arthelper.com').replace(/\/$/, '');
  }
  return window.location.origin;
}

function fullUrl(host: LinkHost, path: string): string {
  return `${originFor(host)}${path}`;
}

async function copy(url: string) {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied');
  } catch {
    toast.error('Could not copy — select the link and copy it instead');
  }
}

function UrlRow({ label, url }: { label: string; url: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 sm:flex-row sm:items-center sm:gap-3">
      <span className="shrink-0 text-xs font-medium text-zinc-500 sm:w-44">{label}</span>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="min-w-0 flex-1 break-all font-mono text-xs text-blue-700 hover:underline"
      >
        {url}
      </a>
      <div className="flex shrink-0 gap-1">
        <Button variant="ghost" size="sm" onClick={() => copy(url)} aria-label={`Copy ${label} link`}>
          <Copy className="size-4" />
        </Button>
        <Button variant="ghost" size="sm" asChild>
          <a href={url} target="_blank" rel="noreferrer" aria-label={`Open ${label} link`}>
            <ExternalLink className="size-4" />
          </a>
        </Button>
      </div>
    </div>
  );
}

function LinkCard({ link }: { link: OneClickLink }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-zinc-900">{link.title}</h3>
        <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600">
          {HOST_LABEL[link.host]}
        </span>
      </div>
      <code className="break-all rounded bg-zinc-900 px-2 py-1 text-xs text-zinc-100">{link.pattern}</code>
      <p className="text-sm text-zinc-700">{link.does}</p>
      {link.notes ? <p className="text-xs text-zinc-500">{link.notes}</p> : null}
      {link.params ? (
        <dl className="grid grid-cols-1 gap-x-3 gap-y-1 text-xs sm:grid-cols-[max-content_1fr]">
          {link.params.map((param) => (
            <div key={param.name} className="contents">
              <dt className="font-mono text-zinc-800">{param.name}</dt>
              <dd className="text-zinc-500">{param.values}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <div className="flex flex-col gap-1.5">
        {link.examples.map((example) => (
          <UrlRow key={example.path} label={example.label} url={fullUrl(link.host, example.path)} />
        ))}
      </div>
      <p className="text-[11px] text-zinc-400">Handled in {link.source}</p>
    </div>
  );
}

type Target = 'surprise' | 'specific' | 'choose';

const TARGETS: Array<{ value: Target; label: string }> = [
  { value: 'surprise', label: 'Surprise me (random)' },
  { value: 'specific', label: 'Specific image ids' },
  { value: 'choose', label: 'Artist picks first' },
];

function chip(active: boolean) {
  const tone = active
    ? 'border-zinc-900 bg-zinc-900 text-white'
    : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50';
  return `rounded-full border px-3 py-1.5 text-sm font-medium ${tone}`;
}

function builtIds(type: AskType, target: Target, first: string, second: string): string[] | null {
  if (target !== 'specific') {
    return [];
  }
  const ids = [first, second].slice(0, imageCount(type)).map((id) => id.trim());
  if (ids.some((id) => !/^[1-9]\d*$/.test(id))) {
    return null;
  }
  if (new Set(ids).size !== ids.length) {
    return null;
  }
  return ids;
}

/** Makes an /ask link for any type, piece and mode — the same `askPath()` emails call. */
function AskBuilder() {
  const [type, setType] = useState<AskType>('rating');
  const [target, setTarget] = useState<Target>('surprise');
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [post, setPost] = useState(false);

  const pair = imageCount(type) === 2;
  const ids = builtIds(type, target, first, second);
  const oneClick = post && target !== 'choose';
  const url = ids ? fullUrl('community', askPath(type, ids, { post: oneClick, choose: target === 'choose' })) : null;

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-zinc-200 bg-white p-4">
      <div>
        <h2 className="text-lg font-semibold text-zinc-900">Build an ask link</h2>
        <p className="text-sm text-zinc-500">
          Pick the kind of post, which piece, and whether it opens the one-click confirm screen. Image ids are My
          Artwork ids.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Label>Kind of post</Label>
        <div className="flex flex-wrap gap-2">
          {ASK_TYPES.map((value) => (
            <button key={value} type="button" className={chip(type === value)} onClick={() => setType(value)}>
              {ASK_LABELS[value]}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label>Which {pair ? 'pieces' : 'piece'}</Label>
        <div className="flex flex-wrap gap-2">
          {TARGETS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={chip(target === option.value)}
              onClick={() => setTarget(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {target === 'specific' ? (
          <div className="flex flex-wrap gap-2">
            <Input
              className="w-40 text-base sm:text-sm"
              inputMode="numeric"
              placeholder={pair ? 'Version A id' : 'Image id'}
              value={first}
              onChange={(e) => setFirst(e.target.value)}
            />
            {pair ? (
              <Input
                className="w-40 text-base sm:text-sm"
                inputMode="numeric"
                placeholder="Version B id"
                value={second}
                onChange={(e) => setSecond(e.target.value)}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      <label className="flex items-center gap-2 text-sm text-zinc-700">
        <input
          type="checkbox"
          className="size-4"
          checked={post}
          disabled={target === 'choose'}
          onChange={(e) => setPost(e.target.checked)}
        />
        Confirm screen (prefilled, one click to publish, then lands on the live post)
        {target === 'choose' ? (
          <span className="text-xs text-zinc-400">— not available when the artist picks</span>
        ) : null}
      </label>
      {url ? (
        <UrlRow label="Your link" url={url} />
      ) : (
        <p className="text-sm text-amber-700">
          Enter {pair ? 'two different image ids' : 'an image id'} (numbers only).
        </p>
      )}
    </div>
  );
}

export default function AdminMarketingLinks() {
  return (
    <AdminLayout
      title="Marketing links"
      description="Every one-click link the product understands — paste them into emails, posts or DMs. Links point at this environment; production is arthelper.com (community) and app.arthelper.ai (app)."
    >
      <div className="flex flex-col gap-10">
        <AskBuilder />
        {LINK_GROUPS.map((group) => (
          <section key={group.id} className="flex flex-col gap-3">
            <div>
              <h2 className="text-xl font-semibold text-zinc-900">{group.title}</h2>
              <p className="text-sm text-zinc-500">{group.blurb}</p>
            </div>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
              {group.links.map((link) => (
                <LinkCard key={link.id} link={link} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </AdminLayout>
  );
}
